import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, TrendingUp, AlertTriangle, CheckCircle, Calendar } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useBudgetStore from '../../store/budgetStore';
import useCategoryStore from '../../store/categoryStore';
import useExpenseStore from '../../store/expenseStore';
import useInitializeFamily from '../../hooks/useInitializeFamily';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { BUDGET_THRESHOLDS } from '../../utils/constants';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { testFirebaseConnection, testCategoriesCollection } from '../../utils/testFirebase';
import { resetCategoriesToLatest, checkCurrentCategories } from '../../utils/resetCategories';

const BudgetOverview = () => {
  // Initialize family data (categories, etc.)
  useInitializeFamily();
  
  const { userProfile } = useAuthStore();
  const { currentBudget, subscribeToCurrentBudget, getBudgetProgress, getBudgetStatus, getRemainingBudget } = useBudgetStore();
  const { categories } = useCategoryStore();
  const { getCurrentMonthExpenses } = useExpenseStore();
  const [showCreateBudget, setShowCreateBudget] = useState(false);

  useEffect(() => {
    if (userProfile?.familyId) {
      subscribeToCurrentBudget(userProfile.familyId);
    }
  }, [userProfile?.familyId, subscribeToCurrentBudget]);

  const currentMonthExpenses = getCurrentMonthExpenses();
  const totalSpent = currentMonthExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'safe': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-orange-600 bg-orange-100';
      case 'exceeded': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'safe': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <AlertTriangle className="h-4 w-4" />;
      case 'critical': return <AlertTriangle className="h-4 w-4" />;
      case 'exceeded': return <AlertTriangle className="h-4 w-4" />;
      default: return <TrendingUp className="h-4 w-4" />;
    }
  };

  if (!currentBudget) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Budget</h1>
            <p className="text-gray-600">Create your first monthly budget</p>
          </div>
          <Button 
            onClick={async () => {
              if (userProfile?.familyId) {
                console.log('🔄 Checking current categories...');
                await checkCurrentCategories(userProfile.familyId);
                
                console.log('🔄 Resetting to latest categories...');
                const result = await resetCategoriesToLatest(userProfile.familyId, userProfile.uid);
                
                if (result.success) {
                  console.log('✅ Categories updated successfully!');
                  // Force refresh the page to load new categories
                  window.location.reload();
                } else {
                  console.error('❌ Failed to update categories:', result.error);
                }
              }
            }}
            variant="outline"
            size="sm"
          >
            Reset to New Categories
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>No Budget Set</CardTitle>
            <CardDescription>
              Create a monthly budget to track your spending and stay on target
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 mb-4">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Create Your First Budget</h3>
              <p className="text-gray-600 mb-4">
                Set spending limits for different categories and track your progress throughout the month.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Categories loaded:</strong> {categories.length} categories
                </p>
                {categories.length > 0 && (
                  <p className="text-xs text-blue-700 mt-1">
                    {categories.map(cat => cat.name).join(', ')}
                  </p>
                )}
              </div>
              
              {totalSpent > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>You've already spent {formatCurrency(totalSpent)} this month.</strong>
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    Creating a budget will help you track and control your spending going forward.
                  </p>
                </div>
              )}

              <Button onClick={() => setShowCreateBudget(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Monthly Budget
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Budget Creation Form */}
        {showCreateBudget && (
          <CreateBudgetForm 
            onCancel={() => setShowCreateBudget(false)}
            currentSpending={totalSpent}
          />
        )}
      </div>
    );
  }

  const overallProgress = getBudgetProgress(currentBudget);
  const overallStatus = getBudgetStatus(overallProgress.percentage);
  const remainingBudget = getRemainingBudget(currentBudget);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budget</h1>
          <p className="text-gray-600">Track your monthly spending progress</p>
        </div>
        <Button variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Adjust Budget
        </Button>
      </div>

      {/* Overall Budget Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Monthly Budget Progress</span>
          </CardTitle>
          <CardDescription>
            {format(new Date(currentBudget.month + '-01'), 'MMMM yyyy')} 
            ({format(currentBudget.startDate.toDate(), 'dd MMM')} - {format(currentBudget.endDate.toDate(), 'dd MMM yyyy')})
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(currentBudget.totalSpent)} / {formatCurrency(currentBudget.totalBudget)}
                </p>
                <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(overallStatus)}`}>
                  {getStatusIcon(overallStatus)}
                  <span>{overallProgress.percentage.toFixed(0)}% used</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(remainingBudget)}
                </p>
                <p className="text-sm text-gray-600">remaining</p>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all duration-300 ${
                  overallStatus === 'exceeded' ? 'bg-red-500' : 
                  overallStatus === 'critical' ? 'bg-orange-500' : 
                  overallStatus === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(overallProgress.percentage, 100)}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Category Breakdown</CardTitle>
          <CardDescription>
            Spending progress by category
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(currentBudget.categories || {}).map(([categoryId, categoryBudget]) => {
              const category = categories.find(cat => cat.id === categoryId);
              if (!category) return null;

              const progress = getBudgetProgress(currentBudget, categoryId);
              const status = getBudgetStatus(progress.percentage);
              const remaining = getRemainingBudget(currentBudget, categoryId);

              return (
                <div key={categoryId} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm"
                        style={{ backgroundColor: category.color }}
                      >
                        {category.icon}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{category.name}</p>
                        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                          <span>{progress.percentage.toFixed(0)}% used</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(categoryBudget.spent)} / {formatCurrency(categoryBudget.budgeted)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatCurrency(remaining)} left
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        status === 'exceeded' ? 'bg-red-500' : 
                        status === 'critical' ? 'bg-orange-500' : 
                        status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Budget Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {currentMonthExpenses.length}
              </p>
              <p className="text-sm text-gray-600">Transactions</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(currentMonthExpenses.reduce((sum, expense) => sum + expense.amount, 0) / Math.max(currentMonthExpenses.length, 1))}
              </p>
              <p className="text-sm text-gray-600">Avg. Transaction</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">
                {Math.max(0, new Date(currentBudget.endDate.toDate()).getDate() - new Date().getDate())}
              </p>
              <p className="text-sm text-gray-600">Days Left</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Simple budget creation component
const CreateBudgetForm = ({ onCancel, currentSpending }) => {
  const { userProfile } = useAuthStore();
  const { createBudget, loading } = useBudgetStore();
  const { categories, loading: categoriesLoading, initializeDefaultCategories, subscribeToCategories, updateToLatestCategories } = useCategoryStore();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [totalBudget, setTotalBudget] = useState(currentSpending * 1.2); // Suggest 20% more than current spending
  const [categoryBudgets, setCategoryBudgets] = useState({});
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  
  // Get current month dates
  const now = new Date();
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const monthName = format(now, 'MMMM yyyy');

  console.log('Categories in CreateBudgetForm:', categories.length, categories);

  // Ensure categories are loaded
  useEffect(() => {
    const loadCategories = async () => {
      if (userProfile?.familyId) {
        // Test Firebase connection
        const connectionOk = await testFirebaseConnection();
        if (!connectionOk) {
          console.error('Firebase connection failed!');
          return;
        }

        // Test categories collection
        const categoryCount = await testCategoriesCollection(userProfile.familyId);
        console.log('Categories in Firestore:', categoryCount);

        if (categories.length === 0) {
          console.log('Manually initializing categories for familyId:', userProfile.familyId);
          try {
            await initializeDefaultCategories(userProfile.familyId, userProfile.uid);
            subscribeToCategories(userProfile.familyId);
          } catch (error) {
            console.error('Error initializing categories:', error);
          }
        }
      }
    };
    
    loadCategories();
  }, [userProfile?.familyId, userProfile?.uid, categories.length, initializeDefaultCategories, subscribeToCategories]);

  const forceRefreshCategories = async () => {
    if (!userProfile?.familyId) return;
    
    setForceRefresh(true);
    try {
      console.log('🔄 Checking current categories in CreateBudgetForm...');
      await checkCurrentCategories(userProfile.familyId);
      
      console.log('🔄 Resetting to latest categories...');
      const result = await resetCategoriesToLatest(userProfile.familyId, userProfile.uid);
      
      if (result.success) {
        console.log('✅ Categories reset successfully!');
        subscribeToCategories(userProfile.familyId);
      } else {
        console.error('❌ Failed to reset categories:', result.error);
      }
    } catch (error) {
      console.error('Error updating categories:', error);
    } finally {
      setForceRefresh(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userProfile?.familyId) return;

    if (calculatedTotal <= 0) {
      alert('Please set budget amounts for at least one category');
      return;
    }

    const budgetData = {
      totalBudget: calculatedTotal,
      categories: categoryBudgets
    };

    const result = await createBudget(userProfile.familyId, userProfile.uid, budgetData);
    if (result.success) {
      onCancel();
    }
  };

  const updateCategoryBudget = (categoryId, amount) => {
    const budgetAmount = parseFloat(amount) || 0;
    
    setCategoryBudgets(prev => {
      const updated = {
        ...prev,
        [categoryId]: {
          budgeted: budgetAmount,
          spent: 0,
          priority: 'need',
          transactions: 0
        }
      };
      
      // Calculate total from all category budgets
      const newTotal = Object.values(updated).reduce((sum, cat) => sum + (cat.budgeted || 0), 0);
      setCalculatedTotal(newTotal);
      setTotalBudget(newTotal); // Auto-update total budget field
      
      return updated;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Calendar className="h-5 w-5" />
          <span>Create Monthly Budget - {monthName}</span>
        </CardTitle>
        <CardDescription>
          Set spending limits from {format(monthStart, 'dd MMM')} to {format(monthEnd, 'dd MMM yyyy')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Monthly Budget (Auto-calculated)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">৳</span>
              <input
                type="number"
                step="0.01"
                value={calculatedTotal.toFixed(2)}
                readOnly
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This total is automatically calculated from your category budgets below
            </p>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Category Budgets</h3>
            {categoriesLoading || categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <span className="text-gray-600 mb-4">
                  {categoriesLoading || forceRefresh ? 'Loading categories...' : 'Categories not loaded'}
                </span>
                {!categoriesLoading && !forceRefresh && (
                  <Button 
                    onClick={forceRefreshCategories}
                    variant="outline" 
                    size="sm"
                  >
                    Reset to New Categories
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {categories
                    .filter((category, index, self) => 
                      index === self.findIndex(c => c.name === category.name)
                    )
                    .map((category) => (
                    <div key={category.id} className="flex items-center space-x-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0"
                        style={{ backgroundColor: category.color }}
                      >
                        {category.icon}
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {category.name}
                        </label>
                      </div>
                      <div className="w-32">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">৳</span>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            onChange={(e) => updateCategoryBudget(category.id, e.target.value)}
                            className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Total from Categories:
                    </span>
                    <span className="text-lg font-bold text-primary">
                      ৳{calculatedTotal.toLocaleString('en-BD')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>
                      {Object.values(categoryBudgets).filter(cat => cat.budgeted > 0).length} of 10 categories budgeted
                    </span>
                    <span>
                      {calculatedTotal > 0 ? 'Ready to create budget' : 'Add amounts to categories'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Tip: As you enter amounts in categories, the total budget updates automatically
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex space-x-4">
            <Button 
              type="submit" 
              disabled={loading || calculatedTotal <= 0} 
              className="flex-1"
            >
              {loading ? 'Creating...' : `Create Budget (৳${calculatedTotal.toLocaleString('en-BD')})`}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default BudgetOverview;
